"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaParser = void 0;
const constant_1 = require("../constant");
const AstNode_1 = require("./AstNode");
/**
 * Parses formulas and generates an Abstract Syntax Tree (AST).
 */
class FormulaParser {
    /**
     * Checks if the provided tokens represent a valid formula.
     * @param tokens - An array of tokens representing the formula.
     * @returns {boolean} True if the tokens form a valid formula; otherwise, false.
     */
    isFormula(tokens) {
        let notOperatorLastIndex = 1;
        let operatorLastIndex = 1;
        const operatorRegex = constant_1.REGEX.formulaOperator;
        for (let index = 0; index < tokens.length; index++) {
            const token = tokens[index];
            const isOperator = operatorRegex.test(String(token));
            const lastIndex = index - 1;
            if (isOperator) {
                operatorLastIndex = index;
            }
            else {
                if (notOperatorLastIndex == lastIndex &&
                    operatorLastIndex != lastIndex) {
                    return false;
                }
                notOperatorLastIndex = index;
            }
        }
        return true;
    }
    /**
     * Checks the syntax of the provided tokens.
     * @param tokens - An array of tokens to check.
     */
    checkSyntax(tokens) {
        this.checkParenthesisSyntax(tokens);
        this.checkOperatorSyntax(tokens);
        this.checkTernaryConditionSyntax(tokens);
    }
    /**
     * Checks the parenthesis syntax of the provided tokens.
     * @param tokens - An array of tokens to check.
     * @throws {Error} Throws an error if there is a parenthesis mismatch.
     */
    checkParenthesisSyntax(tokens) {
        const stack = [];
        tokens.forEach((token) => {
            if (token === constant_1.ParenthesisOpenOperator)
                stack.push(constant_1.ParenthesisOpenOperator);
            if (token === constant_1.ParenthesisCloseOperator) {
                if (stack.length === 0) {
                    throw new Error("Parenthesis mismatch");
                }
                stack.pop();
            }
        });
        if (stack.length !== 0) {
            throw new Error("Incorrect parenthesis disposition.");
        }
    }
    /**
     * Checks the operator syntax of the provided tokens.
     * @param tokens - An array of tokens to check.
     * @throws {Error} Throws an error if there is an operator syntax error.
     */
    checkOperatorSyntax(tokens) {
        const regex = /[+-\/*]{2,}/;
        const expression = tokens.join("");
        if (regex.test(expression)) {
            throw new Error("Incorrect Operator error");
        }
        // When tokens length is equal to one and the token is an string or number without operator , just return because is just an operand
        if (tokens.length == 1 && /\w/.test(expression))
            return;
        const validOperationCheckerRegex = />=|<=|==|!=|&&|\|\||[+-\/*<>%\^][\w\(]/;
        if (!validOperationCheckerRegex.test(expression)) {
            throw new Error("Incorrect Operator position for Operand");
        }
    }
    /**
     * Checks the ternary condition syntax of the provided tokens.
     * @param tokens - An array of tokens to check.
     * @throws {Error} Throws an error if there is a ternary syntax error.
     */
    checkTernaryConditionSyntax(tokens) {
        let ternaryQuestionMarkCount = 0;
        let ternaryColonCount = 0;
        tokens.forEach((token) => {
            if (token === constant_1.QuestionMarkOperator)
                ternaryQuestionMarkCount++;
            if (token === constant_1.ColonOperator)
                ternaryColonCount++;
        });
        if (ternaryQuestionMarkCount !== ternaryColonCount) {
            throw new Error("Incorrect Ternary syntax: unmatched ? and :");
        }
        const ternaryRegex = /[?:]/;
        let expectingCondition = true;
        tokens.forEach((token) => {
            if (ternaryRegex.test(String(token))) {
                if (expectingCondition && token === constant_1.ColonOperator) {
                    throw new Error("Ternary syntax error: found ':' before '?'");
                }
                expectingCondition = !expectingCondition;
            }
        });
    }
    /**
     * Check if the provided tokens is valid formula
     * @param tokens - An array of tokens to verify if is valid formula
     * @returns {boolean} return true when is valid formula and false if not
     */
    isValidFormula(tokens) {
        try {
            if (!this.isFormula(tokens))
                return false;
            this.checkSyntax(tokens);
            return true;
        }
        catch (_a) {
            return false;
        }
    }
    /**
     * Executes the parsing of the provided tokens and generates an AST.
     * @param tokens - An array of tokens to parse.
     * @returns {INode} The root node of the generated AST.
     * @throws {Error} Throws an error if the tokens are not a valid formula.
     */
    execute(tokens) {
        if (tokens.length != 0 && this.isFormula(tokens)) {
            this.checkSyntax(tokens);
            return this.parser(tokens);
        }
        else {
            throw new Error("[Error]: Not formula");
        }
    }
    /**
     * Parses the provided tokens and generates an AST.
     * @param tokens - An array of tokens to parse.
     * @returns {INode} The root node of the generated AST.
     */
    parser(tokens) {
        const postFixExpression = this.infixToPostFix(tokens);
        const result = this.generateAST(postFixExpression);
        return result;
    }
    /**
     * Generates an Abstract Syntax Tree (AST) from the given tokens.
     * @param tokens - An array of tokens to generate the AST from.
     * @returns {INode} The root node of the generated AST.
     */
    generateAST(tokens) {
        const stack = [];
        let counter = 0;
        return this._generateAST(tokens, counter, stack);
    }
    /**
     * Recursively generates the AST based on the provided tokens and the current index.
     * @param tokens - An array of tokens to generate the AST from.
     * @param index - The current index in the tokens array.
     * @param stack - The stack of nodes being built for the AST.
     * @returns {INode} The root node of the generated AST.
     */
    _generateAST(tokens, index, stack = []) {
        const token = tokens[index];
        if (token == undefined)
            return stack[0];
        if (this.isOperatorFirstAndParenthesis(token)) {
            const node = new AstNode_1.AstNode();
            node.operator = token;
            if (this.isArithmeticOperator(token)) {
                node.right = stack.pop();
                node.left = stack.pop();
            }
            else if (this.isComparisonOperator(token)) {
                node.right = stack.pop();
                node.left = stack.pop();
            }
            else if (this.isTernaryOperator(token)) {
                node.isFalse = stack.pop();
                node.isTrue = stack.pop();
                node.condition = stack.pop();
            }
            stack.push(node);
        }
        else {
            const node = new AstNode_1.AstNode();
            if (this.isValue(token)) {
                node.value = token;
            }
            else {
                node.fieldName = token;
            }
            stack.push(node);
        }
        return this._generateAST(tokens, index + 1, stack);
    }
    /**
     * Converts infix tokens to postfix notation.
     * @param tokens - An array of tokens in infix notation.
     * @returns {(string | number)[]} An array of tokens in postfix notation.
     */
    infixToPostFix(tokens) {
        const output = [];
        const operators = [];
        tokens.forEach((token) => {
            if (!this.isOperatorFirstAndParenthesis(token)) {
                output.push(token);
            }
            else {
                const operatorAndParenthesis = String(token);
                const priority = this.priority(operatorAndParenthesis);
                if (operatorAndParenthesis === constant_1.ParenthesisOpenOperator) {
                    operators.push(operatorAndParenthesis);
                }
                else if (operatorAndParenthesis === constant_1.ParenthesisCloseOperator) {
                    while (operators.length > 0 &&
                        operators[operators.length - 1] !== constant_1.ParenthesisOpenOperator) {
                        const operator = operators.pop();
                        if (!(operator.trim() === constant_1.ParenthesisOpenOperator)) {
                            output.push(operator);
                        }
                    }
                    operators.pop();
                }
                else if (operatorAndParenthesis === constant_1.ColonOperator) {
                    while (operators.length > 0 &&
                        operators[operators.length - 1] !== constant_1.QuestionMarkOperator) {
                        output.push(operators.pop());
                    }
                }
                else if (constant_1.Operators.includes(operatorAndParenthesis)) {
                    while (operators.length > 0 &&
                        this.priority(operators[operators.length - 1]) >= priority) {
                        output.push(operators.pop());
                    }
                    operators.push(operatorAndParenthesis);
                }
                else {
                }
            }
        });
        while (operators.length > 0) {
            output.push(operators.pop());
        }
        return output;
    }
    /**
     * Determines the priority level of the given operator.
     *
     * Operators are assigned the following priority levels:
     * 1 - Addition and Subtraction
     * 2 - Multiplication and Division
     * 3 - Comparison and Logical operators
     *
     * @param operator - The operator to evaluate.
     * @returns {number} The priority level of the operator, where higher numbers indicate higher priority.
     */
    priority(operator) {
        if (constant_1.Priority_1_Operator.includes(operator))
            return 1;
        if (constant_1.Priority_2_Operator.includes(operator))
            return 2;
        if (constant_1.Priority_3_Operator.includes(operator))
            return 3;
        if (constant_1.Priority_4_Operator.includes(operator))
            return 4;
        return 0;
    }
    /**
     * Checks if the provided token is an operator or parenthesis.
     *
     * This method considers the following tokens as valid operators:
     * Arithmetic operators: +, -, *, /,^,%
     * Comparison operators: >, <, >=, <=, ==, !=
     * Logical operators: ||, &&
     * Ternary operator: ?
     * Parentheses: (, )
     *
     * @param token - The token to evaluate.
     * @returns  {boolean} -True if the token is an operator or parenthesis; otherwise, false.
     */
    isOperatorFirstAndParenthesis(token) {
        if (constant_1.AllOperators.includes(String(token).trim()))
            return true;
        return false;
    }
    /**
     * Checks if the provided token is an arithmetic operator.
     *
     * The valid arithmetic operators are: +, -, *, /,^,%
     *
     * @param token - The token to evaluate.
     * @returns {boolean} -True if the token is an arithmetic operator; otherwise, false.
     */
    isArithmeticOperator(token) {
        if (constant_1.ArithmeticOperator.includes(token))
            return true;
        return false;
    }
    /**
     * Checks if the provided token is a comparison operator.
     *
     * The valid comparison operators are: >, <, >=, <=, ==, !=, ||, &&
     *
     * @param token - The token to evaluate.
     * @returns {boolean} -  True if the token is a comparison operator; otherwise, false.
     */
    isComparisonOperator(token) {
        if (constant_1.ComparisonOperator.includes(token))
            return true;
        return false;
    }
    /**
     * Checks if the provided token is a ternary operator.
     *
     * The valid ternary operator is: ?
     *
     * @param token - The token to evaluate.
     * @returns {boolean} - True if the token is a ternary operator; otherwise, false.
     */
    isTernaryOperator(token) {
        return constant_1.QuestionMarkOperator === token;
    }
    /**
     * Checks if the provided token is a valid value.
     *
     * A valid value can be a number or a string matching the regex pattern for quoted words.
     *
     * @param token - The token to evaluate.
     * @returns {boolean} - True if the token is a valid value; otherwise, false.
     */
    isValue(token) {
        const valueRegex = /["'][\w]+["']/;
        return typeof token === "number" || valueRegex.test(token) ? true : false;
    }
}
exports.FormulaParser = FormulaParser;
